import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Tab, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

const CategoryTabsBar = ({ tabValue, handleTabChange, menuCategories, language }) => {
  const isRTL = language === 'ar';
  const scrollerRef = useRef(null);
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const { scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);

    // Normalize scrollLeft (RTL browsers can report negative or reversed values)
    let sl = el.scrollLeft;
    if (isRTL && sl === 0) {
      // Some browsers start RTL at 0, some at -maxScroll
      sl = 0;
    }
    const absScroll = Math.abs(sl);

    setCanScrollStart(absScroll > 2);
    setCanScrollEnd(absScroll < maxScroll - 2);
  }, [isRTL]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    // initial + after paint
    updateArrows();
    const raf = requestAnimationFrame(updateArrows);
    const t = setTimeout(updateArrows, 150);

    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);

    // observe size changes (fonts/images loading etc.)
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateArrows);
      ro.observe(el);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
      if (ro) ro.disconnect();
    };
  }, [updateArrows, menuCategories, language]);

  const scrollByDir = (direction) => {
    // direction: -1 = toward visual start, +1 = toward visual end
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.max(160, el.clientWidth * 0.7);

    // In RTL, browsers report scrollLeft differently, but `scrollBy` still
    // works with the *physical* sign. Invert in RTL so "next" moves visually forward.
    const delta = isRTL ? -direction * amount : direction * amount;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  // Whether to render arrows at all (hide on very small width if you want)
  const tabs = [
    { key: 'all', label: isRTL ? 'الكل' : 'All Items' },
    ...menuCategories.map((c) => ({
      key: c.id,
      label: isRTL ? c.name : c.name_en,
    })),
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        minWidth: 0,
        gap: 0.5,
        borderBottom: 1,
        borderColor: '#2D2D2D',
      }}
    >
      {/* START arrow (visually left in LTR, right in RTL) */}
      <IconButton
        onClick={() => scrollByDir(-1)}
        disabled={!canScrollStart}
        size="small"
        aria-label={isRTL ? 'التالي' : 'Previous'}
        sx={{
          flexShrink: 0,
          alignSelf: 'center',
          color: '#FFD700',
          width: 32,
          height: 32,
          transition: 'all 0.2s ease',
          '&.Mui-disabled': { color: '#444' },
          '&:hover': { backgroundColor: 'rgba(255,215,0,0.08)' },
        }}
      >
        {isRTL ? <ChevronRight /> : <ChevronLeft />}
      </IconButton>

      {/* THE ONLY SCROLLER */}
      <Box
        ref={scrollerRef}
        dir={isRTL ? 'rtl' : 'ltr'}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'stretch',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          // smooth momentum on iOS
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Manual tab strip – NO MUI Tabs, so no internal scroll conflict */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            minHeight: 48,
            position: 'relative',
            width: 'max-content',
          }}
        >
          {tabs.map((t, i) => {
            const selected = tabValue === i;
            return (
              <Tab
                key={t.key}
                label={t.label}
                onClick={(e) => handleTabChange(e, i)}
                disableRipple
                sx={{
                  minHeight: 48,
                  minWidth: 'auto',
                  px: { xs: 2, sm: 2.5 },
                  color: selected ? '#FFD700' : '#B3B3B3',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  whiteSpace: 'nowrap',
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: '#FFD700',
                    backgroundColor: 'rgba(255,215,0,0.06)',
                  },
                }}
              />
            );
          })}

          {/* Gold underline under the selected tab */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              height: 3,
              borderRadius: '3px 3px 0 0',
              backgroundColor: '#FFD700',
              transition: 'all 0.25s ease',
              // We use a ref-less approach: let the selected tab render its own underline
              // simpler & more reliable than measuring.
              display: 'none',
            }}
          />
        </Box>
      </Box>

      {/* END arrow (visually right in LTR, left in RTL) */}
      <IconButton
        onClick={() => scrollByDir(1)}
        disabled={!canScrollEnd}
        size="small"
        aria-label={isRTL ? 'السابق' : 'Next'}
        sx={{
          flexShrink: 0,
          alignSelf: 'center',
          color: '#FFD700',
          width: 32,
          height: 32,
          transition: 'all 0.2s ease',
          '&.Mui-disabled': { color: '#444' },
          '&:hover': { backgroundColor: 'rgba(255,215,0,0.08)' },
        }}
      >
        {isRTL ? <ChevronLeft /> : <ChevronRight />}
      </IconButton>
    </Box>
  );
};

export default CategoryTabsBar;